import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MainHeader from '../../header/MainHeader';
import Footer from '../../footer/Footer';
import {
  Card,
  Typography,
  Button,
  Row,
  Col,
  Space,
  Divider,
  Tag,
  Avatar,
  Badge,
  List,
  Statistic,
  Progress,
  Grid,
} from 'antd';
import {
  RocketOutlined,
  CheckCircleOutlined,
  StarOutlined,
  TeamOutlined,
  GlobalOutlined,
  SafetyOutlined,
  TrophyOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  ShopOutlined,
  UserOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { useBreakpoint } = Grid;

interface DealerWelcomeProps {
  onStartRegistration: () => void;
}

const DealerWelcome: React.FC<DealerWelcomeProps> = ({
  onStartRegistration,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const features = [
    {
      icon: <ShopOutlined />,
      title: t('dealer_welcome.pro_presence_title'),
      description: t('dealer_welcome.pro_presence_desc'),
      color: '#1890ff',
    },
    {
      icon: <GlobalOutlined />,
      title: t('dealer_welcome.global_reach_title'),
      description: t('dealer_welcome.global_reach_desc'),
      color: '#52c41a',
    },
    {
      icon: <TeamOutlined />,
      title: t('dealer_welcome.support_title'),
      description: t('dealer_welcome.support_desc'),
      color: '#faad14',
    },
    {
      icon: <SafetyOutlined />,
      title: t('dealer_welcome.secure_title'),
      description: t('dealer_welcome.secure_desc'),
      color: '#f5222d',
    },
  ];

  const benefits = [
    'Increase sales by up to 300%',
    'Access premium customer leads',
    'Professional marketing tools',
    '24/7 customer support',
    'Secure payment processing',
    'Mobile-optimized platform',
  ];

  const steps = [
    {
      number: 1,
      title: 'Company Info',
      description: 'Business details',
      icon: <ShopOutlined />,
    },
    {
      number: 2,
      title: 'Contact Details',
      description: 'Support information',
      icon: <UserOutlined />,
    },
    {
      number: 3,
      title: 'Package Selection',
      description: 'Choose your plan',
      icon: <TrophyOutlined />,
    },
    {
      number: 4,
      title: 'Billing Setup',
      description: 'Payment information',
      icon: <CreditCardOutlined />,
    },
    {
      number: 5,
      title: 'Legal Terms',
      description: 'Compliance & agreements',
      icon: <FileTextOutlined />,
    },
    {
      number: 6,
      title: 'Complete',
      description: 'Final registration',
      icon: <CheckOutlined />,
    },
  ];

  const stats = [
    { title: 'Active Dealers', value: '10,000+', color: '#1890ff' },
    { title: 'Monthly Sales', value: 'ALL 2.5M+', color: '#52c41a' },
    { title: 'Success Rate', value: '95%', color: '#faad14' },
  ];

  const [currentStep, setCurrentStep] = useState(-1);
  const screens = useBreakpoint();

  const getGridConfig = () => {
    if (screens.xxl) return { xs: 4, sm: 4, md: 4, lg: 4, xl: 4, xxl: 4 };
    if (screens.xl) return { xs: 6, sm: 6, md: 6, lg: 6, xl: 6, xxl: 6 };
    if (screens.lg) return { xs: 8, sm: 8, md: 8, lg: 8, xl: 8, xxl: 8 };
    if (screens.md) return { xs: 12, sm: 12, md: 12, lg: 12, xl: 12, xxl: 12 };
    return { xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 24 };
  };

  const getFeatureGridConfig = () => {
    if (screens.lg) return { xs: 12, sm: 12, md: 12, lg: 12, xl: 12, xxl: 12 };
    return { xs: 24, sm: 24, md: 24, lg: 24, xl: 24, xxl: 24 };
  };

  return (
    <div className="auth-page-light home-page-light">
      <MainHeader />
      
      <main className="auth-main" style={{ flexDirection: 'column', height: 'auto', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', paddingTop: '140px' }}>
        <div className="auth-visual-bg"></div>
        
        {/* Hero Section - Split Glass Card */}
        <div className="auth-container mb-5" style={{ maxWidth: '1200px', width: '100%', position: 'relative', zIndex: 10 }}>
          <div className="auth-glass-card">
            {/* Action Side */}
            <div className="auth-form-side">
              <div className="auth-nav-tabs">
                <button className="auth-nav-link" onClick={() => navigate('/dealer-login')}>{t('dealer_welcome.tab_login')}</button>
                <button className="auth-nav-link active">{t('dealer_welcome.tab_register')}</button>
              </div>

              <h1 style={{ fontSize: '2.5rem', fontWeight: 800, marginBottom: '15px' }}>{t('dealer_welcome.title')}</h1>
              <p className="auth-subtitle" style={{ fontSize: '1.1rem', marginBottom: '30px' }}>
                {t('dealer_welcome.subtitle')}
              </p>

              <div className="d-flex flex-column gap-3 mb-5">
                <Button
                  type="primary"
                  size="large"
                  className="btn-hp-primary"
                  onClick={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    onStartRegistration();
                  }}
                  icon={<RocketOutlined />}
                  style={{
                    height: '56px',
                    fontSize: '18px',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none'
                  }}
                >
                  {t('dealer_welcome.cta')}
                </Button>
                
                <div className="text-center mt-2">
                  <Tag icon={<ClockCircleOutlined />} color="success" style={{ borderRadius: '20px', padding: '5px 15px', fontSize: '0.9rem' }}>
                    {t('dealer_welcome.time_note')}
                  </Tag>
                </div>
              </div>

              <div className="auth-footer-text">
                {t('dealer_welcome.have_account')} <Link to="/dealer-login" className="auth-link">{t('dealer_welcome.login_link')}</Link>
                <div className="mt-3 pt-3 border-top" style={{ fontSize: '0.85rem' }}>
                  {t('dealer_welcome.user_prompt')} <Link to="/login" className="auth-link">{t('dealer_welcome.user_link')}</Link>
                </div>
              </div>
            </div>

            {/* Info Side */}
            <div className="auth-info-side">
              <div className="info-content">
                <h3 className="info-title">{t('dealer_welcome.info_title')}</h3>

                <div className="advantage-list">
                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-people-fill text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_welcome.feature1_title')}</h4>
                      <p>{t('dealer_welcome.feature1_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-graph-up-arrow text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_welcome.feature2_title')}</h4>
                      <p>{t('dealer_welcome.feature2_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-star-fill text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_welcome.feature3_title')}</h4>
                      <p>{t('dealer_welcome.feature3_desc')}</p>
                    </div>
                  </div>

                  <div className="advantage-item">
                    <div className="advantage-icon">
                      <i className="bi bi-lightning-fill text-white"></i>
                    </div>
                    <div className="advantage-text">
                      <h4>{t('dealer_welcome.feature4_title')}</h4>
                      <p>{t('dealer_welcome.feature4_desc')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Process Section - Moved to Top */}
        <div className="container pb-5 mb-5" style={{ position: 'relative', zIndex: 10 }}>
          <div className="text-center mb-5">
            <h2 style={{ fontWeight: 800, fontSize: '2.5rem' }}>{t('dealer_welcome.how_title')}</h2>
            <p className="text-muted" style={{ fontSize: '1.1rem' }}>{t('dealer_welcome.how_subtitle')}</p>
          </div>
          
          <Row gutter={[16, 16]} style={{ background: 'rgba(255,255,255,0.4)', padding: '40px', borderRadius: '30px', backdropFilter: 'blur(5px)' }}>
            {steps.map((step, index) => (
              <Col xs={12} md={8} lg={4} key={index}>
                <div 
                  className={`p-4 text-center h-100 ${currentStep === index ? 'active-step' : ''}`}
                  style={{
                    borderRadius: '24px',
                    background: currentStep === index ? 'rgba(254, 90, 29, 0.1)' : 'rgba(255, 255, 255, 0.7)',
                    border: currentStep === index ? '2px solid var(--hp-primary)' : '1px solid rgba(0,0,0,0.05)',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    boxShadow: currentStep === index ? '0 15px 35px rgba(254, 90, 29, 0.15)' : '0 5px 15px rgba(0,0,0,0.02)'
                  }}
                >
                  <div 
                    style={{
                      width: '45px',
                      height: '45px',
                      borderRadius: '50%',
                      background: currentStep === index ? 'var(--hp-primary)' : '#f0f0f0',
                      color: currentStep === index ? 'white' : '#666',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 15px',
                      fontWeight: 800,
                      fontSize: '1.1rem',
                      boxShadow: currentStep === index ? '0 5px 15px rgba(254, 90, 29, 0.3)' : 'none'
                    }}
                  >
                    {step.number}
                  </div>
                  <h5 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: currentStep === index ? 'var(--hp-primary)' : '#262626' }}>{step.title}</h5>
                </div>
              </Col>
            ))}
          </Row>
        </div>

        {/* Features Section */}
        <div className="container mt-5 pt-4 pb-5" style={{ position: 'relative', zIndex: 10 }}>
          <div className="text-center mb-5">
            <h2 style={{ fontWeight: 800, fontSize: '2.5rem' }}>{t('dealer_welcome.benefits_title')}</h2>
            <div style={{ width: '60px', height: '4px', background: 'var(--hp-primary)', margin: '15px auto' }}></div>
          </div>
          
          <Row gutter={[24, 24]}>
            {features.map((feature, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card
                  hoverable
                  style={{
                    height: '100%',
                    borderRadius: '24px',
                    border: '1px solid rgba(0,0,0,0.05)',
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '40px 30px' }}
                >
                  <div 
                    style={{ 
                      width: '70px', 
                      height: '70px', 
                      borderRadius: '20px', 
                      background: `${feature.color}15`, 
                      color: feature.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '28px',
                      marginBottom: '25px',
                      boxShadow: `0 10px 20px ${feature.color}15`
                    }}
                  >
                    {feature.icon}
                  </div>
                  <h4 style={{ fontWeight: 700, marginBottom: '15px', fontSize: '1.25rem' }}>{feature.title}</h4>
                  <p style={{ color: '#666', fontSize: '1rem', lineHeight: 1.6, margin: 0 }}>{feature.description}</p>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </main>

      <Footer />
      
      <style>{`
        .active-step {
          transform: translateY(-8px);
          box-shadow: 0 15px 35px rgba(254, 90, 29, 0.15);
        }
      `}</style>
    </div>
  );
};

export default DealerWelcome;
